/**
 * A manager's guesses at what everybody else will keep.
 *
 * WHY THIS IS NOT JUST A FORM. Two rules govern what may be copied in and what must be typed:
 *
 *   A SUBMITTED SLATE IS PUBLIC and is seeded automatically. The man has told the league what he
 *   is keeping; there is nothing to guess and pretending otherwise would make the tool lie about
 *   its own confidence.
 *
 *   A DRAFT SLATE IS NOT. It is a manager still thinking, and it is never read here — not because
 *   the database would refuse it (it would not; declarations are league-readable so the docket
 *   works) but because using it would turn a private working note into a leak. That is a decision
 *   this module enforces rather than a habit the screen happens to have.
 *
 * AND THE REAL SLATES MOVE. Somebody submits in week one and changes his mind on the 30th, which
 * makes a guess seeded from the old slate quietly wrong. So every set records what it was seeded
 * FROM, and `conflicts` compares that against the slates as they now stand — the difference
 * between "he has changed his slate" and "I guessed differently on purpose" is the whole value of
 * keeping the record.
 */

import type { KeeperDeclaration, MockKeeper, MockKeeperSet } from '../store/types.js';

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
const same = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].map(norm).sort().join('|') === [...b].map(norm).sort().join('|');

/** Only the submitted ones. A draft slate is somebody still thinking. */
export function publicSlates(
  declarations: readonly KeeperDeclaration[],
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const d of declarations) {
    if (d.status !== 'submitted') continue;
    out[d.gm.toUpperCase()] = d.keepers.map((k) => k.player);
  }
  return out;
}

/**
 * A fresh set: every submitted slate copied in, nothing else.
 *
 * The teams that have not declared are absent rather than empty, and the difference matters on
 * the screen — "he has kept nobody" and "you have not guessed yet" are not the same row.
 */
export function seedFrom(
  season: number, gm: string, declarations: readonly KeeperDeclaration[], now: string,
): MockKeeperSet {
  const slates = publicSlates(declarations);
  const byGm: Record<string, MockKeeper[]> = {};
  for (const [who, players] of Object.entries(slates)) {
    const d = declarations.find((x) => x.gm.toUpperCase() === who && x.status === 'submitted');
    byGm[who] = players.map((p) => ({
      player: p,
      fromDeclaration: true,
      franchiseTagged: d?.keepers.find((k) => k.player === p)?.franchiseTagged,
      elevatedTo: d?.keepers.find((k) => k.player === p)?.elevatedTo,
    }));
  }
  return { season, gm: gm.toUpperCase(), byGm, syncedFrom: slates, updatedAt: now };
}

export type ConflictKind =
  /** He has submitted since, and the guess predates it. */
  | 'now-declared'
  /** He submitted, was seeded, and has since changed his slate. */
  | 'slate-changed';

export interface Conflict {
  gm: string;
  kind: ConflictKind;
  /** What the guess currently says. */
  guessed: string[];
  /** What his real slate says now. */
  actual: string[];
  says: string;
}

/**
 * Where the saved guesses no longer match the real slates.
 *
 * ONLY EVER ABOUT SUBMITTED SLATES. A team that has still not declared cannot be in conflict with
 * anything — whatever the manager guessed for him is a guess, which is what it is supposed to be,
 * and flagging it would make the screen nag about the normal case.
 *
 * A guess that was NEVER seeded from a declaration and disagrees with one that has since appeared
 * is `now-declared`: the manager guessed, and the answer has arrived. That is the one people most
 * want told, because it is the row where they were wrong and can find out.
 */
export function conflicts(
  set: MockKeeperSet, declarations: readonly KeeperDeclaration[],
): Conflict[] {
  const slates = publicSlates(declarations);
  const out: Conflict[] = [];

  for (const [who, actual] of Object.entries(slates)) {
    const guessed = (set.byGm[who] ?? []).map((k) => k.player);
    if (same(guessed, actual)) continue;

    const wasSeeded = set.syncedFrom?.[who] !== undefined;
    out.push({
      gm: who,
      kind: wasSeeded ? 'slate-changed' : 'now-declared',
      guessed,
      actual,
      says: wasSeeded
        ? `${who} has changed his slate since you synced.`
        : `${who} has submitted his slate, so this is no longer a guess.`,
    });
  }
  return out.sort((a, b) => a.gm.localeCompare(b.gm));
}

/**
 * Re-sync: every submitted slate replaced with the truth, every guess about an undeclared team
 * left exactly as it was.
 *
 * "Clear and re-sync" is what the screen offers and this is what it does — clear the rows that
 * have been overtaken by facts, and keep the manager's own work on the teams still in play. A
 * re-sync that wiped the guesses too would punish him for pressing the button that tells him he
 * was out of date.
 */
export function resync(
  set: MockKeeperSet, declarations: readonly KeeperDeclaration[], now: string,
): MockKeeperSet {
  const fresh = seedFrom(set.season, set.gm, declarations, now);
  const byGm: Record<string, MockKeeper[]> = { ...set.byGm };
  for (const [who, rows] of Object.entries(fresh.byGm)) byGm[who] = rows;
  return { ...set, byGm, syncedFrom: fresh.syncedFrom, updatedAt: now };
}

/**
 * MAY THE GUESS STAND OVER A SLATE THAT HAS BEEN DECLARED?
 *
 * For anybody else: never. His declaration is a fact about his team and a guess about it is
 * simply out of date.
 *
 * For the acting manager it is not that simple, and the first cut got it wrong in the other
 * direction — his set always won, so a mock set left over from an experiment kept adding Tyjae
 * Spears, Dylan Sampson and Marvin Harrison Jr. to Tim's board days after he had declared six
 * different keepers. Tim, 31 August: "the main draft board is accurate to who I'm keeping but the
 * mock adds tyjae spears and dylan sampson?"
 *
 * The rule is WHICHEVER HE TOUCHED LAST. The sandbox exists so he can try a slate he has not
 * committed to, and taking that away the moment he declares would make the mock useless for the
 * thing it is most used for. But a slate he submitted AFTER last editing the mock is him having
 * made his mind up, and the board should say so. Editing the mock again bumps its stamp and hands
 * the experiment straight back to him.
 */
function setWins(
  gm: string, me: string, set: MockKeeperSet | null, slate: KeeperDeclaration,
): boolean {
  if (gm !== me.toUpperCase()) return false;
  const guessed = Date.parse(set?.updatedAt ?? '');
  const declaredAt = Date.parse(slate.updatedAt ?? '');
  /* An unreadable stamp on either side falls back to the declaration — the fact, not the guess. */
  if (!Number.isFinite(guessed) || !Number.isFinite(declaredAt)) return false;
  return guessed > declaredAt;
}

/**
 * What the mock board should place, per GM.
 *
 * The acting manager's OWN team is never taken from here. His real declaration is a fact he owns,
 * and letting him guess at himself would produce a sandbox where his own keepers are wrong — the
 * one team on the board he cannot be mistaken about.
 */
export function boardKeepers(
  set: MockKeeperSet | null,
  declarations: readonly KeeperDeclaration[],
  me: string,
  /**
   * Farm graduates whose rookie year is mandatory, by GM (2013-III-1 §5).
   *
   * MERGED HERE BECAUSE THE CARD MERGES THEM TOO. `Guess the keepers` shows a graduate ticked and
   * locked whether or not he was ever saved — he is not a choice anybody has — but the saved set
   * only records what the manager actually clicked. The board read the saved set alone, so a team
   * whose only keepers were graduates showed "2 of 8" on the card and nothing on the board, and a
   * manager mocking against it drafted a receiver who was never really in the pool.
   *
   * The alternative was writing the graduates into the set on seed, which would make the record
   * of what he chose a record of what he chose plus what the rules chose for him.
   */
  mandatoryByGm: Readonly<Record<string, readonly string[]>> = {},
): Record<string, Array<{ player: string; franchiseTagged?: boolean; elevatedTo?: number }>> {
  const out: Record<string, Array<{ player: string; franchiseTagged?: boolean; elevatedTo?: number }>> = {};

  /*
   * A DECLARED SLATE IS A FACT, AND IT GOES ON THE BOARD WHETHER OR NOT THE GUESS SET KNOWS.
   *
   * This started from `set.byGm` alone, so the mock board showed the manager's saved guesses and
   * nothing else. `seedFrom` fills the set with the submitted slates AT THE MOMENT IT IS SAVED —
   * which is correct for one afternoon and wrong by the next morning, because during keeper week
   * another slate lands most days. Tim's set held ten guessed players; eight slates and forty
   * keepers had been declared since. The bar above the board read the declarations and said "40
   * keepers seeded from 8 of 10 slates"; the board itself read the set and drew fifteen. Tim, 31
   * August: "mock draft isn't showing declared keepers."
   *
   * So the merge happens at READ time and always: facts first, guesses only where there is no
   * fact. That is the same rule `resync` applies — "every submitted slate replaced with the
   * truth, every guess about an undeclared team left exactly as it was" — except that nobody has
   * to press anything to get it, which is what made the stale board possible.
   */
  for (const d of declarations) {
    if (d.status !== 'submitted') continue;
    out[d.gm.toUpperCase()] = d.keepers.map((k) => ({
      player: k.player, franchiseTagged: k.franchiseTagged, elevatedTo: k.elevatedTo,
    }));
  }

  /*
   * The guesses, over the teams that have not declared — and over HIS OWN, declared or not.
   *
   * His own team is the exception on purpose: the mock is the one place he can try a slate he has
   * not committed to, and overwriting his experiment with the copy the league happens to hold
   * would make the sandbox useless for the thing it is most used for. Everybody else's guess
   * yields to their declaration the moment it exists.
   */
  const declared = new Map(
    declarations.filter((d) => d.status === 'submitted').map((d) => [d.gm.toUpperCase(), d]),
  );
  for (const [who, rows] of Object.entries(set?.byGm ?? {})) {
    const key = who.toUpperCase();
    const slate = declared.get(key);
    if (slate && !setWins(key, me, set, slate)) continue;
    out[key] = rows.map((r) => ({
      player: r.player, franchiseTagged: r.franchiseTagged, elevatedTo: r.elevatedTo,
    }));
  }
  for (const [who, forced] of Object.entries(mandatoryByGm)) {
    const key = who.toUpperCase();
    if (forced.length === 0) continue;
    const rows = out[key] ?? [];
    const have = new Set(rows.map((r) => r.player));
    out[key] = [...rows, ...forced.filter((x) => !have.has(x)).map((player) => ({ player }))];
  }
  /*
   * HIS OWN TEAM COMES FROM THE SET TOO, and it used to come from his declaration instead.
   *
   * The old reasoning was that his own slate is the one team he cannot be wrong about, so a
   * sandbox showing a guess about himself would be worse than useless. True as far as it goes,
   * but it made the mock the one place he could not try a slate — which is the thing a sandbox
   * is for. The seed is still his SUBMITTED slate (`seedFrom` reads nothing else), so he starts
   * from what the league has; from there he is free, and promoting what he arrives at back to a
   * real draft declaration is a separate, deliberate act.
   *
   * Falling back to the declaration when the set has nothing for him keeps a never-seeded mock
   * showing his real keepers rather than an empty column.
   */
  if (!out[me.toUpperCase()]) {
    const mine = declarations.find(
      (d) => d.gm.toUpperCase() === me.toUpperCase() && d.status === 'submitted',
    );
    if (mine) {
      out[me.toUpperCase()] = mine.keepers.map((k) => ({
        player: k.player, franchiseTagged: k.franchiseTagged, elevatedTo: k.elevatedTo,
      }));
    }
  }
  return out;
}
